import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react'
import { useUndoableAction } from '../hooks/useUndoableAction'
import { UndoToast } from '../UndoToast.jsx'

// Wave 36 — app-level undo orchestration. One toast queue, one commit
// scheduler, available to every tab via `useUndo()`.
//
// Why a context: the toast is a global UI affordance (fixed-position
// bottom-right) and only ever shows one message at a time. Hoisting the
// state to App.jsx and prop-drilling `runUndoable` through 6 tabs is
// noise. A context keeps the call site of the destructive action clean
// — `useUndo().runUndoable({...})` — and centralizes the toast render.

export interface UndoableRequest<TResult> {
  /** Async commit invoked after the delay if not undone. */
  commit: () => Promise<TResult>
  /** Optimistic UI tweak applied immediately. Often a setState call. */
  optimisticUpdate?: () => void
  /** Reverses the optimistic update if Undo is clicked. */
  revert?: () => void
  /** Toast text. Should describe what just happened in past tense. */
  message: string
  /** Pending window in ms. Defaults to 8s. */
  delayMs?: number
  /** Surfaced after the commit lands. */
  onCommit?: (result: TResult) => void
  /** Surfaced if commit rejects. */
  onError?: (error: Error) => void
}

interface UndoContextValue {
  runUndoable: <TResult>(request: UndoableRequest<TResult>) => void
}

const UndoContext = createContext<UndoContextValue | null>(null)

export function UndoProvider({ children }: { children: ReactNode }) {
  const [run, state] = useUndoableAction<[UndoableRequest<unknown>], unknown>({
    commit: (request) => request.commit(),
    describe: (request) => request.message,
    onPending: (request) => request.optimisticUpdate?.(),
    onUndo: (request) => request.revert?.(),
    onCommit: (result, request) => request.onCommit?.(result),
    onError: (error, request) => request.onError?.(error),
    // delayMs is read off the request, not the global config — but the
    // hook needs a static value so we pass the maximum band; per-request
    // delays could be implemented by switching the hook's delayMs at call
    // time. For Wave 36 we keep the default 8s for all destructive
    // actions.
    delayMs: 8000,
  })

  const runUndoable = useCallback(
    <TResult,>(request: UndoableRequest<TResult>) => {
      run(request as UndoableRequest<unknown>)
    },
    [run]
  )

  const value = useMemo<UndoContextValue>(
    () => ({ runUndoable }),
    [runUndoable]
  )

  return (
    <UndoContext.Provider value={value}>
      {children}
      <UndoToast
        pending={state.pending}
        message={state.message}
        onUndo={state.undo}
        delayMs={8000}
      />
    </UndoContext.Provider>
  )
}

// Fallback used when the consumer renders outside <UndoProvider>
// (typically a test, a Storybook story, or an isolated harness).
// The fallback runs the optimistic update + commit immediately —
// the action lands as if there were no undo window. The toast is
// intentionally suppressed because there is no provider to render it.
const FALLBACK_UNDO_VALUE: UndoContextValue = {
  runUndoable: <TResult,>(request: UndoableRequest<TResult>) => {
    request.optimisticUpdate?.()
    Promise.resolve(request.commit())
      .then((result) => request.onCommit?.(result))
      .catch((error) =>
        request.onError?.(
          error instanceof Error ? error : new Error(String(error))
        )
      )
  },
}

export function useUndo(): UndoContextValue {
  const ctx = useContext(UndoContext)
  return ctx ?? FALLBACK_UNDO_VALUE
}

// Test-only: exported so unit tests can render the provider without
// requiring `<App />`.
export { UndoContext }
